import React, { useState, useEffect } from 'react';
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CheckCircle,
  Clock,
  Lightning,
  Play,
  Shield,
  Terminal,
  Warning,
  XCircle
} from '@phosphor-icons/react';

/**
 * Admin-only Tracking Diagnostics Panel
 * 
 * WARNING: This is for internal testing only.
 * DO NOT link this in public navigation.
 * DO NOT modify production signup conversion logic.
 */

const TrackingDiagnostics = () => {
  const [logs, setLogs] = useState([]);
  const [gtagStatus, setGtagStatus] = useState(null);
  const [lastFired, setLastFired] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  
  // Google Ads Configuration
  const GOOGLE_ADS_CONFIG = {
    conversionId: 'AW-17962678208',
    conversionLabel: 'M-Z6CJvHIYlcEMDovVC',
    fullSendTo: 'AW-17962678208/M-Z6CJvHIYlcEMDovVC'
  };

  // Check if we're in production
  const isProduction = window.location.hostname === 'manandmachine.app' || 
                       window.location.hostname.includes('manandmachine');

  // Add log entry
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = { timestamp, message, type };
    setLogs(prev => [logEntry, ...prev].slice(0, 50)); // Keep last 50 logs
    
    // Also log to browser console
    const consolePrefix = '[TRACKING TEST]';
    if (type === 'error') {
      console.error(consolePrefix, message);
    } else if (type === 'warning') {
      console.warn(consolePrefix, message);
    } else if (type === 'success') {
      console.log(consolePrefix, '✅', message);
    } else {
      console.log(consolePrefix, message);
    }
  };

  // Check gtag availability
  const checkGtagAvailability = () => {
    addLog('Checking gtag availability...');
    
    const status = {
      gtagExists: typeof window.gtag === 'function',
      dataLayerExists: Array.isArray(window.dataLayer),
      globalTagLoaded: false,
      timestamp: new Date().toISOString()
    };

    // Check if Google tag script is in DOM
    const scripts = document.querySelectorAll('script[src*="googletagmanager"]');
    status.globalTagLoaded = scripts.length > 0;

    setGtagStatus(status);

    if (status.gtagExists) {
      addLog('✅ window.gtag is available', 'success');
    } else {
      addLog('❌ window.gtag is NOT available', 'error');
    }

    if (status.dataLayerExists) {
      addLog(`✅ dataLayer exists with ${window.dataLayer.length} entries`, 'success');
    } else {
      addLog('❌ dataLayer is NOT available', 'error');
    }

    if (status.globalTagLoaded) {
      addLog('✅ Google global tag script is loaded', 'success');
    } else {
      addLog('❌ Google global tag script NOT found in DOM', 'error');
    }

    return status;
  };

  // Fire Google Ads Conversion (with confirmation)
  const fireGoogleAdsConversion = () => {
    if (!gtagStatus?.gtagExists) {
      addLog('Cannot fire conversion - gtag not available', 'error');
      return;
    }

    addLog('Firing Google Ads signup conversion...', 'info');
    
    try {
      window.gtag('event', 'conversion', {
        'send_to': GOOGLE_ADS_CONFIG.fullSendTo
      });
      
      const now = new Date();
      setLastFired(now);
      
      addLog(`✅ Google Ads signup conversion fired successfully`, 'success');
      addLog(`   send_to: ${GOOGLE_ADS_CONFIG.fullSendTo}`, 'info');
      addLog(`   Timestamp: ${now.toISOString()}`, 'info');
      
    } catch (error) {
      addLog(`❌ Error firing conversion: ${error.message}`, 'error');
    }
  };

  // Fire generic gtag event
  const fireGenericEvent = () => {
    if (!gtagStatus?.gtagExists) {
      addLog('Cannot fire event - gtag not available', 'error');
      return;
    }

    addLog('Firing generic gtag test event...', 'info');
    
    try {
      window.gtag('event', 'test_event', {
        'event_category': 'diagnostics',
        'event_label': 'admin_test',
        'value': 0
      });
      
      addLog('✅ Generic gtag event fired successfully', 'success');
      
    } catch (error) {
      addLog(`❌ Error firing event: ${error.message}`, 'error');
    }
  };

  // Run full diagnostics
  const runFullDiagnostics = () => {
    addLog('═══════════════════════════════════════', 'info');
    addLog('Starting Full Tracking Diagnostics...', 'info');
    addLog('═══════════════════════════════════════', 'info');

    // Step 1: Check gtag
    const status = checkGtagAvailability();

    // Step 2: Check Google Ads config
    addLog('', 'info');
    addLog('Google Ads Configuration:', 'info');
    addLog(`   Conversion ID: ${GOOGLE_ADS_CONFIG.conversionId}`, 'info');
    addLog(`   Conversion Label: ${GOOGLE_ADS_CONFIG.conversionLabel}`, 'info');
    addLog(`   Full send_to: ${GOOGLE_ADS_CONFIG.fullSendTo}`, 'info');

    // Step 3: Check for manual test function
    addLog('', 'info');
    if (typeof window.testGoogleAdsSignupConversion === 'function') {
      addLog('✅ Manual test function (testGoogleAdsSignupConversion) is available', 'success');
    } else {
      addLog('⚠️ Manual test function not found in window', 'warning');
    }

    // Step 4: Summary
    addLog('', 'info');
    addLog('═══════════════════════════════════════', 'info');
    addLog('Diagnostics Summary:', 'info');
    
    const allPassed = status.gtagExists && status.dataLayerExists && status.globalTagLoaded;
    
    if (allPassed) {
      addLog('✅ All checks PASSED - Tracking appears to be configured correctly', 'success');
    } else {
      addLog('⚠️ Some checks FAILED - Review issues above', 'warning');
    }
    
    addLog('═══════════════════════════════════════', 'info');
  };

  // Handle conversion button click
  const handleConversionClick = () => {
    if (isProduction && !productionConfirmed) {
      setPendingAction('conversion');
      setShowConfirmModal(true);
    } else {
      fireGoogleAdsConversion();
    }
  };

  // Confirm modal action
  const confirmAction = () => {
    setShowConfirmModal(false);
    if (pendingAction === 'conversion') {
      fireGoogleAdsConversion();
    }
    setPendingAction(null);
  };

  // Initial check on mount
  useEffect(() => {
    checkGtagAvailability();
  }, []);

  // Log type styling
  const getLogStyle = (type) => {
    switch (type) {
      case 'success': return 'text-green-400';
      case 'error': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26] p-6">
      <div className="max-w-5xl mx-auto">
        
        {/* Warning Banner */}
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Warning className="text-yellow-500 flex-shrink-0 mt-0.5" size={24} />
            <div>
              <h3 className="text-yellow-500 font-bold text-lg">
                ⚠️ DEBUG / TEST ONLY - Admin Tracking Diagnostics
              </h3>
              <p className="text-yellow-400/80 text-sm mt-1">
                This panel is for tracking diagnostics only. Triggering live conversion events 
                may affect external reporting and campaign optimisation. Use only in controlled testing.
              </p>
              {isProduction && (
                <p className="text-red-400 text-sm mt-2 font-semibold">
                  🚨 You are in PRODUCTION environment. Live events will affect real campaign data.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-purple-500" size={32} />
            <h1 className="text-3xl font-bold text-white">Tracking Diagnostics</h1>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full">
              ADMIN ONLY
            </span>
          </div>
          <p className="text-gray-400">
            Internal tool for verifying analytics and conversion tracking configuration.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Section 1: Google Ads Status */}
          <div className="bg-[#12131A] border border-[rgba(255,255,255,0.1)] rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Lightning className="text-blue-400" size={20} />
              Google Ads Status
            </h2>
            
            <div className="space-y-3">
              {/* Config Display */}
              <div className="p-3 bg-[#161B26] rounded-lg">
                <p className="text-xs text-gray-500 mb-2">CONFIGURED CONVERSION</p>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Conversion ID:</span>
                    <code className="text-blue-400 text-sm">{GOOGLE_ADS_CONFIG.conversionId}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Label:</span>
                    <code className="text-blue-400 text-sm">{GOOGLE_ADS_CONFIG.conversionLabel}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Full send_to:</span>
                    <code className="text-green-400 text-xs">{GOOGLE_ADS_CONFIG.fullSendTo}</code>
                  </div>
                </div>
              </div>

              {/* Status Checks */}
              <div className="p-3 bg-[#161B26] rounded-lg">
                <p className="text-xs text-gray-500 mb-2">STATUS CHECKS</p>
                <div className="space-y-2">
                  <StatusRow 
                    label="window.gtag exists" 
                    status={gtagStatus?.gtagExists} 
                  />
                  <StatusRow 
                    label="dataLayer exists" 
                    status={gtagStatus?.dataLayerExists} 
                  />
                  <StatusRow 
                    label="Global tag loaded" 
                    status={gtagStatus?.globalTagLoaded} 
                  />
                </div>
              </div>

              {/* Last Fired */}
              {lastFired && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="text-green-400" size={16} />
                    <span className="text-green-400 text-sm">
                      Last fired: {lastFired.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Manual Event Triggers */}
          <div className="bg-[#12131A] border border-[rgba(255,255,255,0.1)] rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Play className="text-green-400" size={20} />
              Manual Event Triggers
            </h2>
            
            <div className="space-y-3">
              {/* Google Ads Conversion Button */}
              <button
                onClick={handleConversionClick}
                disabled={!gtagStatus?.gtagExists}
                className="w-full p-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Lightning size={18} />
                Test Google Ads Signup Conversion
              </button>
              
              {/* Generic Event Button */}
              <button
                onClick={fireGenericEvent}
                disabled={!gtagStatus?.gtagExists}
                className="w-full p-3 bg-[#1a1b26] hover:bg-[#252636] disabled:bg-gray-800 disabled:cursor-not-allowed border border-[rgba(255,255,255,0.1)] rounded-xl text-white font-medium transition-all flex items-center justify-center gap-2"
              >
                <ArrowSquareOut size={16} />
                Test Generic gtag Event
              </button>

              {/* Check Availability Button */}
              <button
                onClick={checkGtagAvailability}
                className="w-full p-3 bg-[#1a1b26] hover:bg-[#252636] border border-[rgba(255,255,255,0.1)] rounded-xl text-white font-medium transition-all flex items-center justify-center gap-2"
              >
                <ArrowsClockwise size={16} />
                Check gtag Availability
              </button>

              {/* Full Diagnostics Button */}
              <button
                onClick={runFullDiagnostics}
                className="w-full p-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Terminal size={18} />
                Run Full Tracking Diagnostics
              </button>

              {/* Production Safety Checkbox */}
              {isProduction && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={productionConfirmed}
                      onChange={(e) => setProductionConfirmed(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-red-500 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-red-400 text-sm">
                      I understand this fires a live conversion event in production and may affect campaign reporting.
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Console Output */}
        <div className="mt-6 bg-[#12131A] border border-[rgba(255,255,255,0.1)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Terminal className="text-gray-400" size={20} />
              Console / Result Output
            </h2>
            <button
              onClick={() => setLogs([])}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Clear Logs
            </button>
          </div>
          
          <div className="bg-[#161B26] rounded-lg p-4 h-80 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <p className="text-gray-600">No logs yet. Run a diagnostic or trigger an event.</p>
            ) : (
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <div key={index} className="flex gap-2">
                    <span className="text-gray-600 flex-shrink-0">[{log.timestamp}]</span>
                    <span className={getLogStyle(log.type)}>{log.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Environment Info */}
        <div className="mt-6 text-center text-gray-600 text-xs">
          Environment: {isProduction ? 'PRODUCTION' : 'Development/Preview'} | 
          Host: {window.location.hostname}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#12131A] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Warning className="text-yellow-500" size={32} />
              <h3 className="text-xl font-bold text-white">Confirm Live Event</h3>
            </div>
            
            <p className="text-gray-300 mb-6">
              You are about to fire a <strong className="text-yellow-400">live Google Ads conversion event</strong>. 
              Continue only if you understand this may affect reporting.
            </p>

            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-6">
              <p className="text-yellow-400 text-sm">
                <strong>Event:</strong> Signup Conversion<br />
                <strong>send_to:</strong> {GOOGLE_ADS_CONFIG.fullSendTo}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingAction(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-xl font-medium transition-colors"
              >
                Fire Test Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Status row component
const StatusRow = ({ label, status }) => {
  const isChecked = status === true;
  const isPending = status === null || status === undefined;
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400 text-sm">{label}</span>
      {isPending ? (
        <span className="text-gray-600 text-sm">—</span>
      ) : isChecked ? (
        <CheckCircle className="text-green-400" size={18} />
      ) : (
        <XCircle className="text-red-400" size={18} />
      )}
    </div>
  );
};

export default TrackingDiagnostics;
